import { Injectable, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { I18nService } from './i18n.service';
import { LoggerService } from './logger.service';

export const INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private monitoringSubscription?: Subscription;
  private authSubscription?: Subscription;

  constructor(
    private authService: AuthService,
    private i18n: I18nService,
    private logger: LoggerService
  ) {
    this.authSubscription = this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.startMonitoring();
      } else {
        this.stopMonitoring();
      }
    });
  }

  private startMonitoring(): void {
    if (this.monitoringSubscription) return;

    const activityEvents$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'click'),
      fromEvent(document, 'touchstart')
    );

    this.monitoringSubscription = activityEvents$.pipe(
      startWith(null),
      switchMap(() => timer(INACTIVITY_TIMEOUT_MS))
    ).subscribe(() => this.handleInactivityTimeout());
  }

  private stopMonitoring(): void {
    this.monitoringSubscription?.unsubscribe();
    this.monitoringSubscription = undefined;
  }

  private handleInactivityTimeout(): void {
    this.logger.warn('InactivityService: 12h of inactivity detected — ending session.');
    const message = this.i18n.translate('notifications.inactivitySessionExpired');
    this.authService.setPendingAlertMessage(message);
    this.authService.clearSessionAndRedirectToLogin();
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    this.authSubscription?.unsubscribe();
  }
}
